// CoreScope's own MeshCore carried-path walk builder — the MeshCore sibling
// of lib/meshview.ts's buildRouteWalk, for GET
// /api/corescope/packets/{hash}/path (backend/amalgamesh/api/routes/
// corescope.py). Genuinely simpler than meshview's Meshtastic route-tree
// walk: no inference, no parent-guessing, no
// confidence tiers, none of the tree-building machinery — CoreScope's
// `hops` array already IS the real, ordered route the packet was carried
// over. There is no tree to walk, no siblings to spread across a launch
// window, and no per-edge epistemic tier to style — a plain linear chain,
// so each hop's own arrival time is simply `hop_index * FLIGHT_MS`
// (constant per-hop speed, one traveller walking the chain in order). This
// is the SAME simplification lib/meshview.ts's buildRouteWalk already
// falls back to for a gap/null-parent edge on a strictly linear traceroute
// chain (see that function's Task 33 comment: "approximated as hop_level *
// FLIGHT_MS ... for a traceroute's own strictly linear chain this is
// exactly the timing an unbroken chain would have produced") — here it
// applies to the WHOLE path, always, because a carried MeshCore route is
// never anything BUT a strictly linear chain; there is no branching case to
// handle at all.
//
// Reuses lib/animation.ts's shared route-walk timing machinery verbatim
// (FLIGHT_MS, routeWalkPathState, MESHVIEW_TRAIL_MS/SLICES) rather than
// building a second animation system — only the GEOMETRY-BUILDING step
// below is CoreScope's own, because its data shape (a flat ordered hop
// list, no confidence/provenance per edge) genuinely differs from
// meshview's route-tree edges.
import type { CorescorePacketPath, CorescorePathHop } from "../api/types";
import { MESHVIEW_TRAIL_MS, MESHVIEW_TRAIL_SLICES } from "./animation";
import { hasUsablePosition } from "./meshview";
import { FLIGHT_MS, chainEdges, edgeTravelMs, walkLifetimeMs, type ChainHop } from "./pathFx";

export interface CorescorePathSegment {
  parent: { lon: number; lat: number };
  child: { lon: number; lat: number };
}

export interface CorescorePath {
  // Exactly one segment per drawn edge, never an accumulated multi-hop chain.
  segments: CorescorePathSegment[];
  startOffsetMs: number;
  totalTravelMs: number; // FLIGHT_MS per hop the edge spans (more than one for a bridged edge)
  /** True for a dim dashed line bridging unresolved hops: drawing only, nothing about it is resolved. */
  bridged: boolean;
}

export interface CorescoreHopFlash {
  lon: number;
  lat: number;
  startOffsetMs: number;
  /** "hop" for a placed relay, "observer" for the station that reported the packet. */
  kind: "hop" | "observer";
}

// One packet's held carried-path walk: AppData.tsx owns it (fetched once per newly-seen has_path CoreScope packet),
// LiveMap.tsx turns elapsed time into paint on its own render clock. `createdAt` may lie in the future: that is how
// a batch of packets is spread out (lib/pathFx.ts's SpreadScheduler); nothing is drawn before it.
export interface CorescorePacketWalk {
  key: string;
  packetHash: string;
  paths: CorescorePath[];
  flashes: CorescoreHopFlash[];
  createdAt: number; // performance.now() when this walk starts (may be later than the fetch)
  maxFinishMs: number;
  /** CoreScope's numeric MeshCore payload type, for the packet type colour. */
  payloadType: number | null;
}

export interface CorescoreWalkOptions {
  payloadType?: number | null;
  /** The reporting station's position, when we know it: the chain ends there. */
  observer?: { lat: number; lon: number } | null;
}

export function corescoreAnimationId(packetHash: string): string {
  return `corescore-${packetHash}`;
}

function hopUsable(h: CorescorePathHop): h is CorescorePathHop & { lat: number; lon: number } {
  return h.resolved && hasUsablePosition({ lat: h.lat, lon: h.lon });
}

// Builds one packet's animated carried-path walk from CoreScope's own path response. Returns null when there is
// nothing drawable at all:
//
// - `has_path` false, or `path_resolution_trustworthy` false: the backend could not safely align its resolved-node
//   list with the carried route, so every hop is reported unresolved. Checked explicitly before touching `hops`.
// - no hop has a usable resolved position (the same hasUsablePosition rule used everywhere else).
//
// Unresolved hops (unknown, ambiguous or unplaced) are never resolved or invented here and never flash. The chain
// stays continuous by drawing a dim dashed line from the last drawn hop to the next drawn hop (lib/pathFx.ts's
// chainEdges: drawing only); with no next placed hop the chain ends at the last one. The reporting station, when its
// position is known, is the end of the chain and lands like any hop. The sender is not in CoreScope's path response,
// so no start marker is drawn for it.
export function buildCorescorePathWalk(resp: CorescorePacketPath, now: number, opts: CorescoreWalkOptions = {}): CorescorePacketWalk | null {
  if (!resp.has_path || !resp.path_resolution_trustworthy) return null;

  const hops = [...resp.hops].sort((a, b) => a.hop_index - b.hop_index);
  const chain: ChainHop[] = hops.map((h) => ({ index: h.hop_index, pos: hopUsable(h) ? { lat: h.lat, lon: h.lon } : null }));
  const placed = chain.filter((h) => h.pos !== null);
  const obs = opts.observer && hasUsablePosition(opts.observer) ? opts.observer : null;
  if (obs) {
    const last = placed[placed.length - 1];
    const sameSpot = last && Math.abs(last.pos!.lat - obs.lat) < 1e-4 && Math.abs(last.pos!.lon - obs.lon) < 1e-4;
    if (!sameSpot) {
      const lastIndex = hops.length ? hops[hops.length - 1].hop_index : -1;
      chain.push({ index: lastIndex + 1, pos: { lat: obs.lat, lon: obs.lon } });
    }
  }
  const drawn = chain.filter((h) => h.pos !== null).sort((a, b) => a.index - b.index);
  if (drawn.length === 0) return null;
  const observerIndex = obs && chain.length > hops.length ? chain[chain.length - 1].index : null;

  const base = drawn[0].index; // the walk starts at its first drawn hop, not at a leading run of unresolved ones
  const paths: CorescorePath[] = [];
  const flashes: CorescoreHopFlash[] = [];
  let maxFinishMs = 0;

  for (const h of drawn) {
    const arrival = (h.index - base) * FLIGHT_MS;
    flashes.push({ lon: h.pos!.lon, lat: h.pos!.lat, startOffsetMs: arrival, kind: h.index === observerIndex ? "observer" : "hop" });
    maxFinishMs = Math.max(maxFinishMs, arrival);
  }
  for (const e of chainEdges(chain)) {
    const startOffsetMs = (e.fromIndex - base) * FLIGHT_MS;
    const totalTravelMs = edgeTravelMs(e);
    paths.push({
      segments: [{ parent: { lon: e.from.lon, lat: e.from.lat }, child: { lon: e.to.lon, lat: e.to.lat } }],
      startOffsetMs,
      totalTravelMs,
      bridged: e.bridged,
    });
    maxFinishMs = Math.max(maxFinishMs, startOffsetMs + totalTravelMs);
  }

  return {
    key: corescoreAnimationId(resp.packet_hash),
    packetHash: resp.packet_hash,
    paths,
    flashes,
    createdAt: now,
    maxFinishMs,
    payloadType: opts.payloadType ?? null,
  };
}

// A walk's own true expiry: when its last landing has finished (lib/pathFx.ts's walkLifetimeMs). A walk whose start
// lies in the future is kept until then.
export function pruneExpiredCorescorePaths(list: CorescorePacketWalk[], now: number): CorescorePacketWalk[] {
  return list.filter((w) => now - w.createdAt < walkLifetimeMs(w.maxFinishMs));
}

// --- path geometry sampling (comet head + trail) ---------------------------
// Same shape as lib/meshview.ts's positionAlongPath/trailSlices, minus the
// confidence/provenance fields those carry — a carried MeshCore path has
// neither concept (see this file's header comment), so there is nothing to
// thread through here; styling is a single fixed value applied at the
// LiveMap.tsx call site (lib/definitions.ts's CORESCORE_PATH_LINE_STYLE).
export function corescorePositionAlongPath(segments: CorescorePathSegment[], elapsedIntoTravel: number, segmentMs: number = FLIGHT_MS): { lon: number; lat: number } {
  const clamped = Math.max(0, elapsedIntoTravel);
  const idx = Math.min(segments.length - 1, Math.floor(clamped / segmentMs));
  const seg = segments[idx];
  const segT = Math.min(1, Math.max(0, (clamped - idx * segmentMs) / segmentMs));
  return {
    lon: seg.parent.lon + (seg.child.lon - seg.parent.lon) * segT,
    lat: seg.parent.lat + (seg.child.lat - seg.parent.lat) * segT,
  };
}

export function corescoreTrailSlices(
  segments: CorescorePathSegment[],
  headElapsedIntoTravel: number,
  segmentMs: number = FLIGHT_MS,
): { from: { lon: number; lat: number }; to: { lon: number; lat: number }; opacityFraction: number }[] {
  const slices: ReturnType<typeof corescoreTrailSlices> = [];
  const points: { lon: number; lat: number }[] = [];
  for (let i = 0; i <= MESHVIEW_TRAIL_SLICES; i++) {
    const t = headElapsedIntoTravel - (i / MESHVIEW_TRAIL_SLICES) * MESHVIEW_TRAIL_MS;
    if (t < 0) break;
    points.push(corescorePositionAlongPath(segments, t, segmentMs));
  }
  for (let i = 0; i < points.length - 1; i++) {
    slices.push({ from: points[i], to: points[i + 1], opacityFraction: 1 - i / MESHVIEW_TRAIL_SLICES });
  }
  return slices;
}
